/**
 * FormationPicker — a visual, tile-based replacement for the Pattern /
 * Progression / Lanes / Coupling dropdowns. Each tile shows a top-down SVG of the
 * parking layout it produces; picking one emits a FormationKey, and the caller
 * maps `formationFields(key)` onto its own field names.
 *
 * Presentational + controlled. `tone="dark"` matches the planner's dark glass
 * panel; the default tone uses shadcn semantic tokens for the builder form.
 */
import { FORMATIONS, formationHelp, type FormationKey } from '@/lib/placement/formations'
import { FieldHelp } from '@/components/ui/field-help'
import { cn } from '@/lib/utils'

type Tone = 'default' | 'dark'

interface Props {
  value: FormationKey | 'custom'
  onChange: (key: FormationKey) => void
  tone?: Tone
  disabled?: boolean
}

const TONES: Record<Tone, { tile: string; selected: string; name: string }> = {
  default: {
    tile: 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    selected: 'border-primary bg-primary/10 text-primary',
    name: 'text-foreground',
  },
  dark: {
    tile: 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white/80',
    selected: 'border-sky-400/50 bg-sky-500/15 text-sky-200',
    name: 'text-white/90',
  },
}

export function FormationPicker({ value, onChange, tone = 'default', disabled }: Props) {
  const t = TONES[tone]
  return (
    <div className="grid grid-cols-2 gap-2">
      {FORMATIONS.map((def) => {
        const active = value === def.key
        return (
          <div key={def.key} className="relative">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(def.key)}
              aria-pressed={active}
              className={cn(
                'flex w-full flex-col items-center gap-1 rounded-lg border p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                active ? t.selected : t.tile
              )}
            >
              <FormationGlyph formation={def.key} />
              <span className={cn('text-[11px] font-semibold', active ? '' : t.name)}>
                {def.name}
              </span>
            </button>
            {/* Sibling, not nested — a button inside a button is invalid HTML. */}
            <span className="absolute right-1 top-1">
              <FieldHelp content={formationHelp(def)} />
            </span>
          </div>
        )
      })}
    </div>
  )
}

/** A single aircraft silhouette (top-down). Nose points away from the tailplane. */
function Jet({ x, y, up = true }: { x: number; y: number; up?: boolean }) {
  const f = up ? 1 : -1
  return (
    <g stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" fill="none">
      {/* fuselage — nose at the -7f end, tail at +7f */}
      <line x1={x} y1={y + 7 * f} x2={x} y2={y - 7 * f} />
      {/* main wing */}
      <line x1={x - 6} y1={y - 1 * f} x2={x + 6} y2={y - 1 * f} />
      {/* tailplane (marks the tail end) */}
      <line x1={x - 3} y1={y + 6 * f} x2={x + 3} y2={y + 6 * f} />
    </g>
  )
}

/** Per-formation top-down diagram. Uses currentColor so tile state tints it. */
function FormationGlyph({ formation }: { formation: FormationKey }) {
  return (
    <svg viewBox="0 0 84 52" className="h-10 w-full" aria-hidden>
      {glyphJets(formation)}
    </svg>
  )
}

function glyphJets(formation: FormationKey): React.ReactNode {
  const xs3 = [16, 42, 68]
  const xsOff = [29, 55]
  switch (formation) {
    case 'single_row':
      return [14, 32, 50, 68].map((x, i) => <Jet key={i} x={x} y={26} />)
    case 'double_row':
      return [
        ...xs3.map((x, i) => <Jet key={`a${i}`} x={x} y={15} />),
        ...xs3.map((x, i) => <Jet key={`b${i}`} x={x} y={38} />),
      ]
    case 'tail_to_tail':
      // noses out: top points up, bottom points down, tails meet at center.
      return [
        ...xs3.map((x, i) => <Jet key={`a${i}`} x={x} y={14} up />),
        ...xs3.map((x, i) => <Jet key={`b${i}`} x={x} y={38} up={false} />),
      ]
    case 'nose_to_nose':
      // noses in + half-slot stagger: top points down, bottom points up, offset.
      return [
        ...xs3.map((x, i) => <Jet key={`a${i}`} x={x} y={14} up={false} />),
        ...xsOff.map((x, i) => <Jet key={`b${i}`} x={x} y={38} up />),
      ]
    case 'nested':
      // zipper: opposing + staggered + pulled close.
      return [
        ...xs3.map((x, i) => <Jet key={`a${i}`} x={x} y={19} up={false} />),
        ...xsOff.map((x, i) => <Jet key={`b${i}`} x={x} y={33} up />),
      ]
    case 'free_pack':
      // densest fill — many same-facing rows.
      return [12, 26, 40].flatMap((y, r) =>
        [12, 30, 48, 66].map((x, c) => <Jet key={`${r}-${c}`} x={x} y={y} />)
      )
  }
}
